import { AfterViewInit, Component, OnInit } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { StatusBar, Style } from '@capacitor/status-bar';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { asyncScheduler, interval, Observable, of } from 'rxjs';
import { delay, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { register } from 'swiper/element/bundle';

import { environment } from '../environments/environment';
import { AppSettings } from './app.settings';
import { ApplicationUser } from './shared/models/ApplicationUser';
import { AppUpdateService } from './shared/services/app-update.service';
import { AuthService } from './shared/services/auth-service';
import { CrashlyticsService } from './shared/services/crashlytics/crashlytics.service';
import { DispatchInitService } from './shared/services/dispatch-init/dispatch-init.service';
import { FirebaseService } from './shared/services/firebase.service';
import { NetworkService } from './shared/services/network.service';
import { NotificationsService } from './shared/services/notifications/notifications.service';
import { PerformanceService } from './shared/services/performance/performance.service';
import { SharedServiceService } from './shared/services/shared-service.service';
import { MainAnalyticsService } from './shared/analytics/main-analytics/main-analytics.service';

register();
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.scss']
})
export class AppComponent implements OnInit, AfterViewInit {

  public hasConnection = true;
  public menuDisabled = false;
  public menu = false;
  public isMinWidthDesktop = false;
  public version = environment.versionNumber;
  public versionApp = `-${environment.versionNumber}${environment.versionCode}`.replace(/\./g, '');
  public currentTime$: Observable<string> = of('');

  private dataStorage: ApplicationUser = {
    token: ''
    , uniqueDeviceID: ''
    , deviceInfo: ''
    , device_id: ''
    , platform: ''
    , create_date: new Date()
  }

  constructor(
    private notificationsS: NotificationsService
    , private dispatchInitS: DispatchInitService
    , private crashlytics: CrashlyticsService
    , private performance: PerformanceService
    , private networkService: NetworkService
    , private sharedS: SharedServiceService
    , private translate: TranslateService
    , private appUpdateS: AppUpdateService
    , private firebaseS: FirebaseService
    , private authS: AuthService
    , private mainAnalyticsS: MainAnalyticsService
  ) {
    this.initApp();
  }

  async initApp() {
    this.firebaseS.initialize();
    await this.setStatusBarConfig();
    await this.sharedS.setDataUsertoStorage();
    this.dispatchInitS.detectUserByToken();
    this.dispatchInitS.detectUserByBOToken();
    document.addEventListener('backbutton', () => { }, false);
    this.sharedS.emitConexionState(false)
    this.dispatchInitS.sendEnvironmentEvent();
    this.dataStorage = await this.authS.updateDeviceUserData();
    this.authS.checkAndStoreNotificationPermission();
    await this.dispatchInitS.getConfigurations();
    await this.performance.setPerformanceCollectionEnabled(true);
    await this.crashlytics.setCrashlyticsCollectionEnabled(true);
    await this.crashlytics.setCrashlyticsUserId(`00-${this.sharedS.getLocalStorage<ApplicationUser>(AppSettings.TOKEN_KEY).uniqueDeviceID}`);
    await this.dispatchInitS.clearDataProgram(this.dataStorage);
    await this.notificationsS.initForegroundNotifications();
    await this.mainAnalyticsS.syncUserPropertiesToFirebase();
  }

  async ngOnInit() {
    this.appUpdateS.appUpdate();
    this.sharedS.menuState.subscribe(count => this.menuDisabled = count);
    this.sharedS.menuVisibleState.subscribe(visible => this.menu = visible);

    this.networkService.initializeNetworkEvents();
    if (Capacitor.isNativePlatform()) {
      await Promise.all([
        ScreenOrientation.lock({ orientation: 'portrait' }),
        this.dispatchInitS.initFCMTokenListener()
      ]);
    }
  }

  ngAfterViewInit() {
    this.dispatchInitS.appPriorityStates();
    this.dispatchInitS.initAfterView();
    this.dispatchInitS.visibilityMenu$().pipe(delay(0)).subscribe(isMinWidthDesktop => this.isMinWidthDesktop = isMinWidthDesktop);
    asyncScheduler.schedule(() => {
      this.currentTime$ = interval(this.translate.instant('Miscellaneous.time-label.update')).pipe(
        startWith(0),
        map(() => moment()
          .utcOffset(this.translate.instant('Miscellaneous.time-label.utcOffset'))
          .format(this.translate.instant('Miscellaneous.time-label.format'))
        ),
        distinctUntilChanged(),
      );
    }, 500);
  }

  private async setStatusBarConfig(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setStyle({ style: Style.Light });
    await StatusBar.setBackgroundColor({ color: '#014f8e' });
    if (Capacitor.getPlatform() === 'android') await EdgeToEdge.setBackgroundColor({ color: '#014f8e' });
  }
}
